import { FC } from "react";
import { useSnackbar, VariantType } from "notistack";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import type { LabAsset } from "../labAssets.types";
import { useUpdateLabAsset } from "../labAssets.query";
import { getLabAssetDisplayName } from "../labAssets.utils";
import DefaultLabAssetForm from "./DefaultLabAssetForm";
import {
  LabAssetCustomValues,
  LabAssetDirtyFields,
  LabAssetPayload,
  diffCustomFields,
  toCustomFieldFormValues,
  toLabAssetFormData,
  toUpdateBody,
} from "./labAssetForm.utils";

interface EditLabAssetFormProps {
  asset: LabAsset;
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

// The LabAsset → form and dirty-fields → PATCH body mappers live in
// labAssetForm.utils.ts (pure, unit-tested); this component only wires them
// to the shared form and the mutation.
const EditLabAssetForm: FC<EditLabAssetFormProps> = ({
  asset,
  width,
  onSubmit,
  onCancel,
  onSubmittingChange,
  onDirtyChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const updateLabAsset = useUpdateLabAsset();

  const handleSubmit = async (
    data: LabAssetPayload,
    dirtyFields: LabAssetDirtyFields,
    customValues: LabAssetCustomValues
  ) => {
    const body = toUpdateBody(data, dirtyFields, asset.asset_type);
    const customFields = diffCustomFields(
      toCustomFieldFormValues(asset.custom_fields),
      customValues
    );
    if (customFields.length > 0) body.custom_fields = customFields;
    // Save is disabled while clean, so this only guards a race; an empty
    // body would be a 400 ("No fields to update").
    if (Object.keys(body).length === 0) return;

    try {
      // Throws on { success: false } with the server's message (400/403/404)
      // and has already opted out of the interceptor toast — one snackbar.
      await updateLabAsset.mutateAsync({ uuid: asset.uuid, body });

      // The API writes the activity-log entry for the update; logging here
      // too would duplicate it into the admin feed.
      onSubmit?.();
      enqueueSnackbar(
        `Asset ${data.name ?? getLabAssetDisplayName(asset)} updated`,
        { variant: "success" as VariantType }
      );
    } catch (error) {
      // Modal stays open with the entered values intact.
      console.error(error);
      enqueueSnackbar(
        error instanceof Error && error.message
          ? error.message
          : "An error occurred while updating this asset. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <DefaultLabAssetForm
        assetType={asset.asset_type}
        DefaultFormData={toLabAssetFormData(asset)}
        DefaultCustomFields={asset.custom_fields}
        issuedTo={asset.issued_to}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onSubmittingChange={onSubmittingChange}
        onDirtyChange={onDirtyChange}
        disableSubmitWhenClean
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};

export default EditLabAssetForm;
